// frontend/src/pages/superadmin/SuperAdminMigrations.jsx
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const saApi = axios.create({ baseURL: API });
saApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('sa_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
import { Play, PlayCircle, CheckCircle2, AlertCircle, RefreshCw, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

export default function SuperAdminMigrations() {
  const [migrations, setMigrations] = useState([]);
  const [error, setError] = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [running,    setRunning]    = useState(null); // nom de la migration en cours
  const [runningAll, setRunningAll] = useState(false);
  const [logs,       setLogs]       = useState({});   // { name: [lines] }
  const [confirmAction, setConfirmAction] = useState(null); // { type:'single', name } | { type:'all' } | null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await saApi.get('/superadmin/migrations');
      setMigrations(Array.isArray(r.data.migrations) ? r.data.migrations : []);
      setError(null);
    } catch(e) {
      console.error('Migrations load error:', e);
      setError(e.response?.data?.error || e.message || 'Erreur chargement');
      setMigrations([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runMigration = async (name) => {
    setRunning(name);
    setLogs(l => ({ ...l, [name]: ['⏳ Exécution en cours…'] }));
    try {
      const r = await saApi.post(`/superadmin/migrations/${name}/run`);
      setLogs(l => ({ ...l, [name]: r.data.logs || ['✓ Succès'] }));
      toast.success(`Migration ${name} appliquée`);
      load();
    } catch(e) {
      const errLogs = e.response?.data?.logs || [`✗ Erreur : ${e.response?.data?.error || e.message}`];
      setLogs(l => ({ ...l, [name]: errLogs }));
      toast.error(`Erreur : ${e.response?.data?.error || 'Echec migration'}`);
    } finally { setRunning(null); }
  };

  // "Tout exécuter" appelle maintenant l'endpoint unitaire (déjà fiable, testé
  // manuellement) une migration à la fois côté frontend, au lieu d'un seul
  // gros appel serveur qui lance les 6 dans la même requête HTTP — ce dernier
  // pouvait dépasser le timeout du reverse-proxy sur un lot un peu long et
  // remontait un échec générique alors que le backend continuait de tourner.
  // Chaque migration progresse et s'affiche indépendamment, exactement comme
  // en lançant les boutons "Exécuter" un par un.
  // Petite pause entre deux migrations : laisse le temps à la connexion DB
  // précédente d'être proprement relâchée côté backend avant la suivante,
  // et évite tout effet de bord lié à des requêtes HTTP trop rapprochées
  // (proxy/CORS) — sans incidence perceptible pour l'utilisateur.
  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  const runAll = async () => {
    setRunningAll(true);
    setError(null);
    let done = 0;
    try {
      for (const m of pending) {
        setRunning(m.name);
        setLogs(l => ({ ...l, [m.name]: ['⏳ Exécution en cours…'] }));
        try {
          const r = await saApi.post(`/superadmin/migrations/${m.name}/run`);
          setLogs(l => ({ ...l, [m.name]: r.data.logs || ['✓ Succès'] }));
          done++;
        } catch(e) {
          // Détail complet remonté (message + code Postgres éventuel) pour
          // pouvoir diagnostiquer précisément un futur blocage sur >1 migration.
          const code = e.response?.data?.code ? ` [${e.response.data.code}]` : '';
          const message = (e.response?.data?.error || e.message || 'Échec de la migration') + code;
          const errLogs = e.response?.data?.logs?.length ? e.response.data.logs : [`✗ Erreur : ${message}`];
          setLogs(l => ({ ...l, [m.name]: errLogs }));
          setError(`Arrêt sur "${m.name}" : ${message}`);
          toast.error(`Arrêt sur "${m.name}" — voir le détail ci-dessous`);
          return;
        }
        await sleep(300);
      }
      toast.success(`${done} migration${done>1?'s':''} appliquée${done>1?'s':''}`);
    } catch(e) {
      // Filet de sécurité : ne devrait jamais arriver (toutes les erreurs
      // réseau/HTTP sont déjà interceptées ci-dessus), mais évite un écran
      // blanc si un cas imprévu survient.
      setError(`Erreur inattendue : ${e.message}`);
      toast.error('Erreur inattendue — voir le détail');
    } finally {
      setRunning(null);
      setRunningAll(false);
      load();
    }
  };

  const pending = (migrations || []).filter(m => !m.applied && !m.missing);
  const applied = (migrations || []).filter(m => m.applied);

  return (
    <>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>Migrations SQL</h2>
          <p style={{ color:'var(--text-3)', fontSize:13, marginTop:4 }}>
            {pending.length > 0
              ? <span style={{ color:'var(--amber)', fontWeight:600 }}>
                  ⚠ {pending.length} migration{pending.length>1?'s':''} en attente
                </span>
              : <span style={{ color:'var(--teal)' }}>✓ Base de données à jour</span>}
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {pending.length > 0 && (
            <button className="btn btn-primary btn-sm" onClick={() => setConfirmAction({ type:'all' })} disabled={runningAll || running}>
              {runningAll
                ? <span className="spinner" style={{width:12,height:12}}/>
                : <><PlayCircle size={13}/> Tout exécuter ({pending.length})</>}
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }}/> Actualiser
          </button>
        </div>
      </div>

      {/* Migrations en attente */}
      {pending.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <div style={{ fontSize:13, fontWeight:700, color:'var(--amber)',
            marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
            <Clock size={13}/> En attente ({pending.length})
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {pending.map(m => (
              <div key={m.name} style={{ borderRadius:10, overflow:'hidden',
                border:'1px solid rgba(245,166,35,0.3)', background:'rgba(245,166,35,0.04)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px' }}>
                  <AlertCircle size={15} color="var(--amber)" style={{ flexShrink:0 }}/>
                  <span style={{ flex:1, fontFamily:'monospace', fontSize:13, fontWeight:600 }}>
                    {m.name}
                  </span>
                  <button className="btn btn-primary btn-sm"
                    onClick={() => setConfirmAction({ type:'single', name: m.name })}
                    disabled={running === m.name || runningAll}>
                    {running === m.name
                      ? <span className="spinner" style={{width:12,height:12}}/>
                      : <><Play size={11}/> Exécuter</>}
                  </button>
                </div>
                {logs[m.name] && (
                  <div style={{ padding:'10px 16px', borderTop:'1px solid rgba(245,166,35,0.2)',
                    background:'rgba(0,0,0,0.2)', fontFamily:'monospace', fontSize:12 }}>
                    {logs[m.name].map((line, i) => (
                      <div key={i} style={{
                        color: line.startsWith('✓') ? 'var(--teal)'
                          : line.startsWith('✗') ? 'var(--red)'
                          : 'var(--text-2)',
                        marginBottom:2,
                      }}>
                        {line}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Migrations appliquées */}
      <div>
        <div style={{ fontSize:13, fontWeight:700, color:'var(--text-3)',
          marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
          <CheckCircle2 size={13}/> Appliquées ({applied.length})
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Fichier</th>
                <th>Appliquée le</th>
                <th>Par</th>
                <th/>
              </tr>
            </thead>
            <tbody>
              {applied.map(m => (
                <tr key={m.name}>
                  <td>
                    <span style={{ fontFamily:'monospace', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                      <CheckCircle2 size={12} color="var(--teal)"/>
                      {m.name}
                      {m.missing && (
                        <span style={{ fontSize:10, color:'var(--red)',
                          background:'rgba(255,77,109,0.1)', padding:'1px 6px', borderRadius:99 }}>
                          fichier absent
                        </span>
                      )}
                    </span>
                  </td>
                  <td style={{ fontSize:12, color:'var(--text-3)' }}>
                    {m.appliedAt
                      ? format(new Date(m.appliedAt), 'dd/MM/yyyy HH:mm', { locale:fr })
                      : '—'}
                  </td>
                  <td style={{ fontSize:12, color:'var(--text-3)' }}>{m.appliedBy || '—'}</td>
                  <td>
                    {logs[m.name] && (
                      <div style={{ fontFamily:'monospace', fontSize:11, color:'var(--teal)' }}>
                        {logs[m.name].slice(-1)[0]}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {error && (
        <div style={{ padding:'12px 16px', borderRadius:10, marginTop:16,
          background:'rgba(255,77,109,0.08)', border:'1px solid rgba(255,77,109,0.3)',
          fontSize:13, color:'var(--red)' }}>
          ✗ {error}
        </div>
      )}
      {loading && migrations.length === 0 && (
        <div style={{ textAlign:'center', padding:32 }}>
          <span className="spinner"/>
        </div>
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.type === 'all' ? 'Exécuter toutes les migrations' : 'Exécuter la migration'}
          message={
            confirmAction.type === 'all'
              ? `Exécuter ${pending.length} migration${pending.length>1?'s':''} en attente, dans l'ordre ?\n\n` +
                `L'exécution s'arrête automatiquement à la première erreur. Action irréversible.`
              : `Exécuter la migration "${confirmAction.name}" ?\n\nCette action est irréversible.`
          }
          confirmLabel="Exécuter"
          danger
          onConfirm={() => {
            const action = confirmAction;
            setConfirmAction(null);
            if (action.type === 'all') runAll(); else runMigration(action.name);
          }}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </>
  );
}
